/*
 * Copyright (c) 2024 Paul Ostrowski. All rights reserved.
 *
 * This software is the proprietary information of Paul Ostrowski.
 * Unauthorized copying, modification, or distribution of this code, via any medium, is strictly prohibited.
 * This code is proprietary and confidential.
 *
 * For licensing inquiries, contact Paul Ostrowski at postrowski@gmail.com.
*/

import React, { useState, useEffect } from 'react';
import PreferenceService from 'services/PreferenceService';

export type TourId = 'main' | 'search' | 'spells' | 'mm2024';

interface TourSelectorProps {
  isOpen: boolean;
  onClose: () => void;
  onSelect: (tour: TourId) => void;
}

const TOURS: { id: TourId; name: string; desc: string }[] = [
  { id: 'main',   name: 'Main Tour',                 desc: "A walkthrough of Monster Forge's main features." },
  { id: 'search', name: 'Advanced Monster Search',   desc: 'Find monsters by CR, environment, and keyword / phrase qualifiers.' },
  { id: 'spells', name: 'Spellcasting',              desc: 'Add spellcasting to a monster: choose a class, build a spellbook, and prepare spells.' },
  { id: 'mm2024', name: '2024 Monster Manual Style', desc: 'The 2024 layout: initiative as a skill, combined immunities, per-attribute saves, and gear.' },
];

// Opened from the main '?' tour button. Lets the user pick which guided tour to take, and shows a
// checkbox next to any tour they have completed (read from IndexedDB via PreferenceService).
const TourSelector: React.FC<TourSelectorProps> = ({ isOpen, onClose, onSelect }) => {
  type Progress = { completed: boolean; step: number | null; total: number | null };
  const [progress, setProgress] = useState<Record<string, Progress>>({});

  useEffect(() => {
    if (!isOpen) {
      return;
    }
    let cancelled = false;
    Promise.all(TOURS.map(async (t) => {
      const [c, s, total] = await Promise.all([
        PreferenceService.getUserPreference(`tour-completed-${t.id}`),
        PreferenceService.getUserPreference(`tour-step-${t.id}`),
        PreferenceService.getUserPreference(`tour-total-${t.id}`),
      ]);
      const p: Progress = {
        completed: c === 'true',
        step: s != null ? parseInt(s, 10) : null,
        total: total != null ? parseInt(total, 10) : null,
      };
      return [t.id, p] as [string, Progress];
    })).then((entries) => {
      if (!cancelled) {
        setProgress(Object.fromEntries(entries));
      }
    });
    return () => { cancelled = true; };
  }, [isOpen]);

  if (!isOpen) {
    return null;
  }
  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" style={{ width: '520px', height: 'auto', minHeight: '260px' }} onClick={(e) => e.stopPropagation()}>
        <span className="modal-title" style={{ display: 'block', textAlign: 'center' }}>Choose a Tour</span>
        <p style={{ fontSize: 'small' }}>Pick a guided tour to take.</p>
        <div>
          {TOURS.map((t) => (
            <div key={t.id} style={{ display: 'flex', alignItems: 'stretch', gap: '10px', marginBottom: '8px' }}>
              <span style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: '44px', flexShrink: 0 }}
                    title={progress[t.id]?.completed ? "You've completed this tour"
                           : (progress[t.id]?.step != null ? "How much of this tour you've seen" : '')}>
                {(() => {
                  const p = progress[t.id];
                  if (!p) return null;
                  if (p.completed) {
                    return <input type="checkbox" checked readOnly style={{ width: '36px', height: '36px', margin: 0 }} />;
                  }
                  if (p.step != null && p.total != null) {
                    return <span style={{ fontSize: 'large', fontWeight: '700', whiteSpace: 'nowrap' }}>{Math.round(((p.step + 1) / p.total) * 100)}%</span>;
                  }
                  return null;
                })()}
              </span>
              <button type="button" onClick={() => onSelect(t.id)}
                      style={{ flex: 1, textAlign: 'left', padding: '8px 10px', cursor: 'pointer', borderRadius: '6px' }}>
                <b>{t.name}</b>
                <div style={{ fontSize: 'small', fontWeight: 'normal' }}>{t.desc}</div>
              </button>
            </div>
          ))}
        </div>
        <div className="modal-actions" style={{ textAlign: 'center' }}>
          <button type="button" onClick={onClose}>Close</button>
        </div>
      </div>
    </div>
  );
};

export default TourSelector;
